import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { Loader2, Shield, ShieldCheck, RefreshCw } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import { supabase } from "@/integrations/supabase/client";

export function MFASettings() {
  const { mfaEnabled, enrollMFA, verifyMFA, unenrollMFA } = useAuth();
  const [isReEnrolling, setIsReEnrolling] = useState(false);
  const [enrollmentData, setEnrollmentData] = useState<{
    id: string;
    uri: string;
  } | null>(null);
  const [verificationCode, setVerificationCode] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [factors, setFactors] = useState<any[]>([]);

  useEffect(() => {
    loadFactors();
  }, [mfaEnabled]);

  const loadFactors = async () => {
    const { data, error } = await supabase.auth.mfa.listFactors();
    if (!error) {
      setFactors(data.all.filter(f => f.factor_type === "totp" && f.status === "verified"));
    }
  };

  const handleReEnroll = async () => {
    if (!confirm("Re-enrolling will replace your current authenticator. You will need to scan a new QR code. Continue?")) return;

    setIsLoading(true);

    // Unenroll existing factor first
    const verifiedFactor = factors.find(f => f.factor_type === "totp" && f.status === "verified");
    if (verifiedFactor) {
      const { error: unenrollError } = await unenrollMFA(verifiedFactor.id);
      if (unenrollError) {
        toast.error(unenrollError.message || "Failed to remove existing factor");
        setIsLoading(false);
        return;
      }
    }

    // Also clean up any unverified factors from incomplete previous enrollments
    const { data: allFactors } = await supabase.auth.mfa.listFactors();
    if (allFactors) {
      const unverifiedFactors = allFactors.all.filter(
        (f) => f.factor_type === "totp" && f.status === "unverified"
      );
      for (const factor of unverifiedFactors) {
        await supabase.auth.mfa.unenroll({ factorId: factor.id });
      }
    }

    // Enroll new factor
    const { data, error } = await enrollMFA();
    if (error) {
      toast.error(error.message || "Failed to start re-enrollment");
    } else {
      setEnrollmentData({
        id: data.id,
        uri: data.totp.uri,
      });
      setIsReEnrolling(true);
    }
    setIsLoading(false);
  };

  const handleVerifyReEnrollment = async () => {
    if (!enrollmentData || verificationCode.length !== 6) return;

    setIsLoading(true);
    const { data: challenge, error: challengeError } = await supabase.auth.mfa.challenge({
      factorId: enrollmentData.id,
    });

    if (challengeError) {
      toast.error(challengeError.message || "Failed to create challenge");
      setIsLoading(false);
      return;
    }

    const { error: verifyError } = await verifyMFA(
      enrollmentData.id,
      challenge.id,
      verificationCode
    );

    if (verifyError) {
      toast.error(verifyError.message || "Invalid verification code");
    } else {
      toast.success("Authenticator app updated successfully");
      setIsReEnrolling(false);
      setEnrollmentData(null);
      setVerificationCode("");
      loadFactors();
    }
    setIsLoading(false);
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-full bg-green-100 text-green-600">
              <ShieldCheck className="h-6 w-6" />
            </div>
            <div>
              <CardTitle>Multi-Factor Authentication</CardTitle>
              <CardDescription>
                MFA is required for all admin accounts. Your account is secured with an authenticator app.
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {!isReEnrolling ? (
            <div className="space-y-4">
              <div className="p-4 bg-muted rounded-lg border flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Shield className="h-5 w-5 text-primary" />
                  <div>
                    <p className="font-medium">Authenticator App (TOTP)</p>
                    <p className="text-xs text-muted-foreground">Status: Active — Required</p>
                  </div>
                </div>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleReEnroll}
                  disabled={isLoading}
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Change Device
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                MFA is mandatory for all admin accounts and cannot be disabled. If you need to switch 
                to a new phone or authenticator app, use the "Change Device" button above.
              </p>
            </div>
          ) : (
            <div className="space-y-6 flex flex-col items-center animate-in fade-in slide-in-from-bottom-4">
              <div className="text-center space-y-2">
                <h3 className="font-medium">Scan New QR Code</h3>
                <p className="text-sm text-muted-foreground">
                  Scan this QR code with your new authenticator app to re-link your account.
                </p>
              </div>

              {enrollmentData && (
                <div className="p-4 bg-white rounded-xl shadow-inner border">
                  <QRCodeSVG value={enrollmentData.uri} size={200} />
                </div>
              )}

              <div className="text-center space-y-4 w-full max-w-xs">
                <div className="space-y-2">
                  <p className="text-sm font-medium">Verify Code</p>
                  <p className="text-xs text-muted-foreground">
                    Enter the 6-digit code from your new app to confirm
                  </p>
                </div>

                <div className="flex justify-center">
                  <InputOTP
                    maxLength={6}
                    value={verificationCode}
                    onChange={(value) => setVerificationCode(value)}
                  >
                    <InputOTPGroup>
                      <InputOTPSlot index={0} />
                      <InputOTPSlot index={1} />
                      <InputOTPSlot index={2} />
                      <InputOTPSlot index={3} />
                      <InputOTPSlot index={4} />
                      <InputOTPSlot index={5} />
                    </InputOTPGroup>
                  </InputOTP>
                </div>

                <div className="flex flex-col gap-2 pt-2">
                  <Button 
                    onClick={handleVerifyReEnrollment} 
                    disabled={isLoading || verificationCode.length !== 6}
                    className="w-full"
                  >
                    {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Confirm New Device
                  </Button>
                  <Button 
                    variant="ghost" 
                    onClick={() => {
                      setIsReEnrolling(false);
                      setEnrollmentData(null);
                    }}
                    className="w-full"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
